import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { ShieldCheck, Send, Square, AlertCircle, RotateCcw } from 'lucide-react'
import { SocShell } from '../components/soc/SocShell'
import { cn } from '../lib/utils'
import {
  streamConciergeChat,
  sendConciergeChat,
  ConciergeStreamError,
  type ConciergeChatRequest,
} from '../lib/concierge'

// The Concierge chat — the client "talks to" their security team here in plain
// language (PRODUCT_VISION_agent_team.md). Replies stream token-by-token over
// SSE for a live, human feel; if the stream cannot be reached we fall back to a
// single non-streaming request so the input is never dead. When a live stream
// breaks mid-answer we keep the partial text on screen and offer an honest
// "try again" that re-sends over the fallback.

type MessageState = 'streaming' | 'error' | undefined

interface ChatMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  state: MessageState
}

interface PendingRetry {
  request: ConciergeChatRequest
  assistantId: number
}

const GREETING =
  "Hi, I'm your DigitalWatch concierge. Ask me anything about what's " +
  'happening with your security, and I’ll put the team’s notes in plain ' +
  'English.'

export function ConciergePage() {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [status, setStatus] = useState<'idle' | 'streaming' | 'error'>('idle')
  const [retry, setRetry] = useState<PendingRetry | null>(null)

  const idRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const nextId = () => ++idRef.current
  const busy = status === 'streaming'

  useEffect(() => {
    const el = scrollRef.current
    // scrollTo is unimplemented in jsdom; guard so tests (and any headless
    // env) don't throw on autoscroll.
    if (el && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight })
    }
  }, [messages])

  const patch = useCallback(
    (id: number, content: string, state: MessageState) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, content, state } : m)),
      )
    },
    [],
  )

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || busy) return

      // Prior turns become the model's context — drop any errored/blank turns.
      const history = messages
        .filter((m) => m.state !== 'error' && m.content.length > 0)
        .map((m) => ({ role: m.role, content: m.content }))

      const userMsg: ChatMessage = {
        id: nextId(),
        role: 'user',
        content: trimmed,
        state: undefined,
      }
      const assistantId = nextId()
      setMessages((prev) => [
        ...prev,
        userMsg,
        { id: assistantId, role: 'assistant', content: '', state: 'streaming' },
      ])
      setInput('')
      setRetry(null)
      setStatus('streaming')

      const request: ConciergeChatRequest = {
        message: trimmed,
        conversation_history: history,
      }
      const controller = new AbortController()
      abortRef.current = controller

      let streamed = ''
      try {
        await streamConciergeChat(request, {
          signal: controller.signal,
          onChunk: (_delta, full) => {
            streamed = full
            patch(assistantId, full, 'streaming')
          },
          onDone: (full) => patch(assistantId, full, undefined),
        })
        setStatus('idle')
      } catch (err) {
        const aborted = controller.signal.aborted
        const partial =
          err instanceof ConciergeStreamError ? err.partial : streamed

        // Abort, or an SSE error event mid-answer: keep the partial text and
        // let the client re-send over the fallback.
        if (aborted || (err instanceof ConciergeStreamError && partial)) {
          patch(assistantId, partial, 'error')
          setRetry({ request, assistantId })
          setStatus('error')
          return
        }

        // Couldn't reach the stream (or it broke before a word arrived) — fall
        // back to the non-streaming endpoint so the input is never dead.
        try {
          const full = await sendConciergeChat(request)
          patch(assistantId, full, undefined)
          setStatus('idle')
        } catch {
          patch(assistantId, partial, 'error')
          setRetry({ request, assistantId })
          setStatus('error')
        }
      } finally {
        abortRef.current = null
      }
    },
    [busy, messages, patch],
  )

  const retryViaFallback = useCallback(async () => {
    if (!retry) return
    const { request, assistantId } = retry
    setRetry(null)
    setStatus('streaming')
    patch(assistantId, '', 'streaming')
    try {
      const full = await sendConciergeChat(request)
      patch(assistantId, full, undefined)
      setStatus('idle')
    } catch {
      patch(assistantId, '', 'error')
      setRetry({ request, assistantId })
      setStatus('error')
    }
  }, [retry, patch])

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void send(input)
  }

  return (
    <SocShell current="concierge" mainClassName="flex flex-1 w-full flex-col min-h-0">

      {/* Status header — who the client is talking to */}
      <div className="border-b border-brass/15 bg-white">
        <div className="max-w-[480px] mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-success/10 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-success" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-navy leading-tight">
              Your Concierge
            </h1>
            <p className="text-xs text-gray-500">
              Plain-English answers from your security team
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 w-full max-w-[480px] mx-auto flex flex-col min-h-0">
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-5 space-y-3"
          aria-live="polite"
        >
          {/* Greeting bubble — always the team's warm first word */}
          <MessageBubble
            role="assistant"
            content={GREETING}
            state={undefined}
          />

          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              role={m.role}
              content={m.content}
              state={m.state}
            />
          ))}
        </div>

        {status === 'error' && retry && (
          <div className="px-4 pb-2">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-danger/30 bg-danger-light px-3 py-2 text-sm text-danger">
              <span className="flex items-center gap-2 min-w-0">
                <AlertCircle
                  className="w-4 h-4 shrink-0"
                  aria-hidden="true"
                />
                <span className="truncate">
                  Lost the connection.
                </span>
              </span>
              <button
                type="button"
                onClick={() => void retryViaFallback()}
                className="tap-min flex items-center gap-1.5 rounded-lg bg-danger px-3 py-1.5 text-xs font-semibold text-white hover:bg-danger/90 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                Try again
              </button>
            </div>
          </div>
        )}

        {busy && (
          <div className="px-4 pb-1">
            <p
              className="text-xs text-gray-500 flex items-center gap-1.5"
              role="status"
            >
              <span className="inline-flex gap-0.5" aria-hidden="true">
                <Dot delay={0} />
                <Dot delay={0.15} />
                <Dot delay={0.3} />
              </span>
              Your analyst is writing…
            </p>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="border-t border-brass/15 bg-white px-4 py-3 flex items-end gap-2"
        >
          <label htmlFor="concierge-input" className="sr-only">
            Message your concierge
          </label>
          <input
            id="concierge-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={busy}
            placeholder={
              busy ? 'Your analyst is writing…' : 'Ask about your security…'
            }
            className="flex-1 rounded-xl border border-gray-200 bg-cream px-3 py-2.5 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brass/40 disabled:opacity-60"
            autoComplete="off"
          />
          {busy ? (
            <button
              type="button"
              onClick={stop}
              className="tap-min flex items-center justify-center rounded-xl bg-gray-100 px-3 py-2.5 text-gray-600 hover:bg-gray-200 transition-colors"
              aria-label="Stop"
            >
              <Square className="w-4 h-4" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className="tap-min flex items-center justify-center rounded-xl bg-navy px-3 py-2.5 text-white hover:bg-navy/90 disabled:opacity-40 transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </form>
      </div>
    </SocShell>
  )
}

function MessageBubble({
  role,
  content,
  state,
}: {
  role: 'user' | 'assistant'
  content: string
  state: MessageState
}) {
  const isUser = role === 'user'
  const empty = content.length === 0
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn('flex', isUser ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words',
          isUser
            ? 'bg-navy text-white rounded-br-sm'
            : 'bg-white text-navy border border-brass/15 rounded-bl-sm',
          state === 'error' && 'border-danger/30',
        )}
      >
        {empty && state === 'streaming' ? (
          <span className="text-gray-400">…</span>
        ) : (
          content
        )}
        {empty && state === 'error' && (
          <span className="text-gray-400 italic">
            (nothing came through)
          </span>
        )}
      </div>
    </motion.div>
  )
}

function Dot({ delay }: { delay: number }) {
  return (
    <motion.span
      className="w-1 h-1 rounded-full bg-brass"
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 0.9, repeat: Infinity, delay }}
    />
  )
}
